import { useMemo, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Mail, Lock } from "lucide-react";

import { request } from "@/lib/api";
import { useAuth } from "@/state/AuthContext";
import { defaultPostLoginPath } from "@/utils/postLoginPath";
import { MarketingSiteLink } from "@/pages/auth/MarketingSiteLink";
import {
  AuthCard,
  AuthPageShell,
  authFieldWithIconClass,
  authLinkClass,
  authPrimaryButtonClass,
} from "@/pages/auth/AuthPageShell";

function safeReturnUrl(v: string | null) {
  if (!v) return null;
  if (!v.startsWith("/")) return null;
  if (v.startsWith("//")) return null;
  return v;
}

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  const params = useMemo(() => new URLSearchParams(loc.search), [loc.search]);
  const returnUrl = safeReturnUrl(params.get("returnUrl"));
  const sessionExpired = params.get("reason") === "session_expired";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setEmailNotVerified(false);
    setResendMessage(null);
    setSubmitting(true);
    try {
      const outcome = await login(email, password);
      // MFA-HIDE-1: backend cannot emit an MFA challenge. Do not open the
      // unregistered challenge page (it POSTed a 404 on both environments).
      if (!outcome.ok) {
        setErr("Sign in failed");
        return;
      }
      // Role landing via defaultPostLoginPath (ADMIN → /home, MEMBER/VIEWER → /inbox).
      // Incomplete Admin onboarding is enforced later by DashboardLayout → /onboarding.
      // Do not call authenticated APIs here: a 401 → refresh failure in `api.ts`
      // triggers `window.location.assign("/login")`, which full-reloads and clears
      // in-memory JWTs (cross-site cookie mode), reproducing "instant kick out".
      nav(returnUrl || defaultPostLoginPath(outcome.user), { replace: true });
    } catch (e: any) {
      const code = e?.response?.data?.code;
      if (code === "EMAIL_NOT_VERIFIED") {
        setEmailNotVerified(true);
        setErr(null);
      } else if (code === "USER_MISSING_ORGANIZATION") {
        // D-69: every membership of this account has ended (an org admin
        // removed them from their last organization).
        setErr("You no longer have access to any organization. Contact your administrator.");
      } else if (code === "ACCOUNT_LOCKED") {
        const until = e?.response?.data?.lockedUntil || e?.response?.data?.cooldownUntil;
        setErr(
          typeof until === "string"
            ? `This account is temporarily locked. Try again after ${until}.`
            : "This account is temporarily locked. Try again later.",
        );
      } else {
        const msg = e?.response?.data?.message || e?.message || "Sign in failed";
        setErr(String(msg));
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResendVerification() {
    if (resendCooldown || !email) return;
    setResendMessage(null);
    try {
      await request.post("/auth/resend-verification", { email });
      setResendMessage("Verification email sent. Please check your inbox.");
      setResendCooldown(true);
      setTimeout(() => setResendCooldown(false), 60000);
    } catch (e: any) {
      if (e?.response?.status === 429) {
        setResendMessage("Too many requests. Please wait before trying again.");
      } else {
        setResendMessage("If an account with this email exists, you will receive a verification email.");
      }
    }
  }

  return (
    <AuthPageShell
      testId="login-page"
      title="Enterprise Sign-in"
      subtitle={
        <>
          Need access to Zephix?{" "}
          <Link className={authLinkClass} to="/signup">
            Request access
          </Link>
        </>
      }
    >
      <AuthCard>
        {sessionExpired && (
          <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            Your session ended. Please sign in again to continue.
          </div>
        )}

        {err && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700" role="alert">
            {err}
          </div>
        )}

        {emailNotVerified && (
          <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-4">
            <p className="text-sm font-medium text-amber-800">Verify your email to continue</p>
            <p className="mt-1 text-sm text-amber-700">
              We sent a verification link to <strong>{email}</strong>. Please check your inbox and
              click the link to activate your account.
            </p>
            <button
              type="button"
              onClick={handleResendVerification}
              disabled={resendCooldown}
              className={`mt-3 text-sm ${authLinkClass} disabled:text-slate-400 disabled:cursor-not-allowed disabled:no-underline`}
            >
              {resendCooldown ? "Resend available in 60s" : "Resend verification email"}
            </button>
            {resendMessage && <p className="mt-2 text-xs text-slate-600">{resendMessage}</p>}
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-6">
          <div>
            <label htmlFor="login-email" className="block text-sm font-medium text-slate-700">
              Email Address
            </label>
            <div className="mt-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="h-5 w-5 text-slate-400" aria-hidden="true" />
              </div>
              <input
                id="login-email"
                className={authFieldWithIconClass}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                type="email"
                required
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label htmlFor="login-password" className="block text-sm font-medium text-slate-700">
                Password
              </label>
              <Link className={`text-xs ${authLinkClass}`} to="/forgot-password">
                Forgot password?
              </Link>
            </div>
            <div className="mt-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-slate-400" aria-hidden="true" />
              </div>
              <input
                id="login-password"
                className={authFieldWithIconClass}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type="password"
                autoComplete="current-password"
                required
              />
            </div>
          </div>

          <button type="submit" disabled={submitting} className={authPrimaryButtonClass}>
            {submitting ? "Signing in…" : "Sign In"}
          </button>
        </form>

        <p className="mt-6 text-center">
          <MarketingSiteLink />
        </p>
      </AuthCard>
    </AuthPageShell>
  );
}
